import { useEffect, useState } from "react";
import { Layout } from "@/components/layout/Layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart,
  DonutChart,
  LineChart,
} from "@/components/ui/charts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowUpRight, Download, Calendar, TrendingUp, Building, Users, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import ManagerLayout from "@/components/layout/ManagerLayout";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/api";

// AED currency formatter
const formatAED = (amount: number) => {
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency: 'AED',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

export default function ManagerEarningsPage() {
  const [timeframe, setTimeframe] = useState("month");
  const [activeTab, setActiveTab] = useState("overview");
  const { toast } = useToast();
  
  // Fetch earnings data from API
  const { data: earningsData, isLoading, error } = useQuery({
    queryKey: ['manager/earnings', timeframe],
    queryFn: async () => {
      const response = await apiRequest('GET', `manager/earnings?timeframe=${timeframe}`);
      // if (!response.ok) {
      //   throw new Error('Failed to fetch earnings data');
      // }
      // return response.json();
      return response;
    },
    enabled: true,
    refetchOnWindowFocus: false,
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive"
      });
    }
  });

  return (
    <ManagerLayout>
      <div className="container py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-1">Earnings Dashboard</h1>
            <p className="text-muted-foreground">Track your property income and financial performance</p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center gap-4">
            <Select value={timeframe} onValueChange={setTimeframe}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Select timeframe" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="quarter">This Quarter</SelectItem>
                <SelectItem value="year">This Year</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              <span>Export</span>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {isLoading ? (
            <div className="col-span-4 flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : earningsData ? (
            <>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6">
                        <text x="6" y="16" fontSize="10" fontWeight="bold">د.إ</text>
                      </svg>
                    </div>
                    <ArrowUpRight className="h-4 w-4 text-green-500" />
                  </div>
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground">Total Earnings</p>
                    <h3 className="text-2xl font-bold">{formatAED(earningsData.totalEarnings)}</h3>
                    <p className="text-sm text-green-500 mt-1">
                      {earningsData.percentChange > 0 ? '+' : ''}{earningsData.percentChange.toFixed(1)}% from last period
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-orange-500">
                      <Calendar className="h-6 w-6" />
                    </div>
                    <span className="text-sm font-medium text-orange-500 bg-orange-100 px-2 py-1 rounded-full">Pending</span>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground">Pending Payouts</p>
                    <h3 className="text-2xl font-bold">{formatAED(earningsData.pendingPayouts)}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {earningsData.recentTransactions.filter(t => t.status === "confirmed").length} booking(s)
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-500">
                      <TrendingUp className="h-6 w-6" />
                    </div>
                    <span className="text-sm font-medium">YTD</span>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground">Year-to-Date</p>
                    <h3 className="text-2xl font-bold">{formatAED(earningsData.yearToDateEarnings)}</h3>
                    <p className="text-sm text-green-500 mt-1">
                      {earningsData.ytdPercentChange > 0 ? '+' : ''}{earningsData.ytdPercentChange.toFixed(1)}% from last year
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-500">
                      <Building className="h-6 w-6" />
                    </div>
                    <span className="text-sm font-medium">{earningsData.activeProperties} Properties</span>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground">Completed Bookings</p>
                    <h3 className="text-2xl font-bold">{earningsData.completedBookings}</h3>
                    <p className="text-sm text-green-500 mt-1">
                      {timeframe === "all" ? "Total bookings" : "This period"}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="col-span-4 flex justify-center items-center py-12">
              <p className="text-muted-foreground">No earnings data available</p>
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="properties">Properties Performance</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Monthly Earnings</CardTitle>
                  <CardDescription>Your earnings trend over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-96">
                    {isLoading ? (
                      <div className="h-full flex justify-center items-center">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : earningsData ? (
                      <LineChart
                        data={earningsData.monthlyData}
                        index="month"
                        categories={["earnings"]}
                        colors={["#0ea5e9"]}
                        valueFormatter={(value) => formatAED(value)}
                        yAxisWidth={80}
                      />
                    ) : (
                      <div className="h-full flex justify-center items-center">
                        <p className="text-muted-foreground">No monthly data available</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Earnings Breakdown</CardTitle>
                  <CardDescription>Where your earnings come from</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-72">
                    {isLoading ? (
                      <div className="h-full flex justify-center items-center">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : earningsData ? (
                      <DonutChart
                        data={earningsData.breakdownData}
                        index="name"
                        category="value"
                        valueFormatter={(value) => formatAED(Math.abs(value))}
                        colors={["#0ea5e9", "#ef4444", "#f59e0b", "#10b981"]}
                      />
                    ) : (
                      <div className="h-full flex justify-center items-center">
                        <p className="text-muted-foreground">No breakdown data available</p>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 space-y-3">
                    {earningsData?.breakdownData?.map((item, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center">
                          <div 
                            className="w-3 h-3 rounded-full mr-2"
                            style={{ backgroundColor: index === 0 ? "#0ea5e9" : index === 1 ? "#ef4444" : index === 2 ? "#f59e0b" : "#10b981" }}
                          ></div>
                          <span className="text-sm">{item.name}</span>
                        </div>
                        <span className={`text-sm font-medium ${item.value < 0 ? "text-red-500" : ""}`}>
                          {item.value < 0 ? "-" : ""}{formatAED(Math.abs(item.value))}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="properties">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Property Performance</CardTitle>
                  <CardDescription>Earnings by property</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-96">
                    {isLoading ? (
                      <div className="h-full flex justify-center items-center">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : earningsData ? (
                      <BarChart
                        data={earningsData.propertyPerformance}
                        index="property"
                        categories={["earnings"]}
                        colors={["#0ea5e9"]}
                        valueFormatter={(value) => formatAED(value)}
                        yAxisWidth={80}
                      />
                    ) : (
                      <div className="h-full flex justify-center items-center">
                        <p className="text-muted-foreground">No property performance data available</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Top Performing Property</CardTitle>
                    <CardDescription>Your highest-earning property</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {isLoading ? (
                      <div className="flex justify-center items-center py-4">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      </div>
                    ) : earningsData?.topProperty ? (
                      <div className="flex items-center">
                        <div className="w-16 h-16 rounded bg-muted flex items-center justify-center mr-4">
                          <Building className="h-8 w-8 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-medium">{earningsData.topProperty.name}</h3>
                          <p className="text-sm text-muted-foreground">{earningsData.topProperty.bookings} bookings</p>
                          <p className="text-lg font-bold text-primary mt-1">{formatAED(earningsData.topProperty.earnings)}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-muted-foreground">No property data available</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Agent Commissions</CardTitle>
                    <CardDescription>Total paid to rental agents</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {isLoading ? (
                      <div className="flex justify-center items-center py-4">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      </div>
                    ) : earningsData?.agentCommissions ? (
                      <div className="flex items-center">
                        <div className="w-16 h-16 rounded bg-red-100 flex items-center justify-center mr-4">
                          <Users className="h-8 w-8 text-red-500" />
                        </div>
                        <div>
                          <h3 className="font-medium">{earningsData.agentCommissions.count} Agent(s)</h3>
                          <p className="text-sm text-muted-foreground">{earningsData.agentCommissions.bookings} referred bookings</p>
                          <p className="text-lg font-bold text-red-500 mt-1">-{formatAED(earningsData.agentCommissions.amount)}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-muted-foreground">No commission data available</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <CardTitle>Recent Transactions</CardTitle>
                <CardDescription>Your recent earnings and payouts</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex justify-center items-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : earningsData?.recentTransactions?.length ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Property</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {earningsData.recentTransactions.map((transaction) => (
                        <TableRow key={transaction.id}>
                          <TableCell>
                            <span className="font-medium">{new Date(transaction.date).toLocaleDateString('en-AE', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          </TableCell>
                          <TableCell>{transaction.property}</TableCell>
                          <TableCell>{transaction.type || 'Booking Payment'}</TableCell>
                          <TableCell className="text-right">
                            <span className="font-medium">{formatAED(transaction.amount)}</span>
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                              transaction.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                              transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">No transaction data available</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="analytics">
            <Card>
              <CardHeader>
                <CardTitle>Earnings Analytics</CardTitle>
                <CardDescription>Detailed analysis of your earnings</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center p-12">
                  <h3 className="text-lg font-medium mb-2">Advanced Analytics Coming Soon</h3>
                  <p className="text-muted-foreground">We're working on providing more detailed analytics for your properties.</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </ManagerLayout>
  );
}